// Admin console: Update Knowledge (/admin/update-knowledge).
//
// What Pluto does NOT know yet, in one list, and one button that teaches it:
// convert the Drive file to .md, then embed it into pgvector. An item leaves
// the list as soon as both halves are done, and carries the badge "Diproses"
// while the run has it.
//   header  plain spacing, hairline below  h1, count, Update Knowledge ke Pluto
//   notice  notice                         the last run's result, or an error
//   items   rows                           table from 1024, stacked rows below
//   empty   empty state                    "Pengetahuan Pluto sudah terbaru."
//
// The run outlives the request (a large regulation takes minutes), so the
// screen polls while `running` is true instead of waiting on the POST.
import { useEffect, useState } from "react";
import { formatCount, formatDateTime } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL } from "../lib/labels";
import * as Update from "../knowledge-update-api";
import { Badge, EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

/** How often the list is refreshed while an update is running. */
export const POLL_MS = 4000;

const KIND_LABEL: Record<Update.UpdateItem["kind"], string> = {
  drive: "Berkas Google Drive",
  entry: "Dokumen pengetahuan",
};

export function categoryText(item: Update.UpdateItem): string {
  if (!item.category) return "Belum ditentukan";
  return KNOWLEDGE_CATEGORY_LABEL[item.category as keyof typeof KNOWLEDGE_CATEGORY_LABEL] ?? item.category;
}

/** The sentence under the title: what is waiting, or that nothing is. */
export function leadText(view: Update.KnowledgeUpdateView | null): string {
  if (!view) return "Memuat pengetahuan baru";
  const n = view.items.length;
  if (n === 0) return "Pengetahuan Pluto sudah terbaru. Tidak ada berkas baru yang perlu diproses.";
  const busy = view.items.filter((i) => i.stage === "diproses").length;
  const head = `${formatCount(n)} pengetahuan baru belum dipelajari Pluto.`;
  return view.running ? `${head} ${formatCount(busy)} sedang diproses.` : head;
}

/** The Indonesian summary of a finished run. */
export function resultText(r: NonNullable<Update.KnowledgeUpdateView["lastResult"]>): string {
  const parts: string[] = [];
  if (r.converted > 0) parts.push(`${formatCount(r.converted)} berkas diubah menjadi dokumen pengetahuan`);
  if (r.indexed > 0) parts.push(`${formatCount(r.indexed)} dokumen dipelajari Pluto (${formatCount(r.chunks)} potongan teks)`);
  if (parts.length === 0) parts.push("Tidak ada pengetahuan baru yang diproses");
  if (r.failures.length > 0) parts.push(`${formatCount(r.failures.length)} gagal`);
  return `${parts.join(", ")}. Selesai ${formatDateTime(r.startedAt)}.`;
}

export function UpdateKnowledgeScreen() {
  useTitle("Update Knowledge");
  const view = useAsync(() => Update.getKnowledgeUpdate(), []);
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const data = view.data;
  const running = data?.running ?? false;

  // Poll only while something is actually being processed; the interval is
  // cleared the moment the run ends, so an idle screen makes no requests.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => view.reload(), POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  async function start() {
    setStarting(true);
    setProblem(null);
    try {
      await Update.startKnowledgeUpdate();
    } catch (err) {
      setProblem(Update.errorMessage(err));
    } finally {
      setStarting(false);
      view.reload();
    }
  }

  const items = data?.items ?? [];
  const canStart = !running && !starting && items.length > 0;

  return (
    <>
      <PageHeader
        divided
        title="Update Knowledge"
        lead={leadText(data)}
        actions={
          <button type="button" className="btn" onClick={start} aria-busy={starting || running || undefined} disabled={!canStart}>
            <Glyph name="refresh" size={20} />
            <span>{running ? "Sedang memproses" : "Update Knowledge ke Pluto"}</span>
          </button>
        }
      />

      <section className="region" aria-label="Pengetahuan baru">
        {problem ? (
          <Notice tone="danger" title="Update belum berjalan">
            <p>{problem}</p>
          </Notice>
        ) : null}

        {view.error ? (
          <Notice
            tone="danger"
            title="Daftar pengetahuan baru belum dapat dimuat"
            action={
              <button type="button" className="btn btn-secondary" onClick={view.reload}>
                Muat ulang
              </button>
            }
          >
            <p>{Update.errorMessage(view.error)}</p>
          </Notice>
        ) : null}

        {data?.lastResult ? (
          <Notice tone={data.lastResult.failures.length > 0 ? "warning" : "success"} title="Hasil update terakhir">
            <p>{resultText(data.lastResult)}</p>
            {data.lastResult.failures.length > 0 ? (
              <ul className="rows uk-failures">
                {data.lastResult.failures.map((f) => (
                  <li key={`${f.name}:${f.reason}`} className="cell-meta">
                    {f.name}: {f.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </Notice>
        ) : null}

        {view.loading && !data ? (
          <SkeletonRows count={4} label="Memuat pengetahuan baru" />
        ) : items.length === 0 && !view.error ? (
          <EmptyState
            icon="tickCircle"
            title="Pengetahuan Pluto sudah terbaru"
            body="Setiap berkas di folder Basis Pengetahuan sudah diproses dan dipelajari Pluto. Berkas baru akan muncul di sini setelah disinkronkan dari Google Drive."
          />
        ) : items.length > 0 ? (
          <>
            <div className="records-table" data-testid="uk-table">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Pengetahuan</th>
                    <th scope="col">Kategori</th>
                    <th scope="col">Asal</th>
                    <th scope="col">Status</th>
                    <th scope="col">Catatan</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.key} data-stage={item.stage}>
                      <td className="kb-title-cell">
                        <span className="cell-title">{item.name}</span>
                      </td>
                      <td className="nowrap">{categoryText(item)}</td>
                      <td className="nowrap">{KIND_LABEL[item.kind]}</td>
                      <td className="nowrap">
                        <Badge tone={item.stage === "diproses" ? "info" : "neutral"}>{item.stage === "diproses" ? "Diproses" : "Baru"}</Badge>
                      </td>
                      <td>{item.reason ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="rows records-list">
              {items.map((item) => (
                <li key={item.key} className="row" data-stage={item.stage}>
                  <span className="row-main">
                    <span className="row-title">{item.name}</span>
                    <span className="row-meta">
                      {categoryText(item)}, {KIND_LABEL[item.kind]}
                    </span>
                    {item.reason ? <span className="row-meta">{item.reason}</span> : null}
                  </span>
                  <span className="row-side">
                    <Badge tone={item.stage === "diproses" ? "info" : "neutral"}>{item.stage === "diproses" ? "Diproses" : "Baru"}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </>
  );
}
